import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  Loader2,
  ShieldAlert,
  ArrowLeft,
  Users,
  MessageSquare,
  Send,
  Upload,
  X,
  Building2,
  FileDown,
  CheckCircle2,
  PlayCircle,
  Sparkles,
  FileText,
  UserPlus,
  Search,
  Pencil,
  Clock,
  Video,
  VideoOff,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { openProtectedFile } from "@/components/employee/tabHelpers";
import { formatDateHu } from "@/components/DeductionFormDialog";
import { DateTimeInput } from "@/components/ui/datetime-input";

const STATUS_TONE = {
  new: "bg-slate-100 text-slate-700 ring-slate-200",
  investigating: "bg-amber-50 text-amber-800 ring-amber-200",
  finalized: "bg-indigo-50 text-indigo-800 ring-indigo-200",
  closed: "bg-emerald-50 text-emerald-800 ring-emerald-200",
};
const STATUS_LABEL = {
  new: "Új bejegyzés",
  investigating: "Kivizsgálás alatt",
  finalized: "Véglegesítve",
  closed: "Lezárva",
};

const PARTICIPANT_ROLES = [
  "Sérült",
  "Szemtanú",
  "Tanú",
  "Munkavédelmi felelős",
  "Munkáltató képviselője",
  "Elsősegélynyújtó",
  "Felügyelő",
  "Egyéb",
];

const Th = ({ children, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`whitespace-nowrap px-4 py-3 ${className}`}>{children}</td>
);

const StatusBadge = ({ status }) => (
  <span
    data-testid={`accident-status-${status}`}
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ${STATUS_TONE[status] || ""}`}
  >
    {STATUS_LABEL[status] || status}
  </span>
);

const formatDateTimeHu = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return d.toLocaleString("hu-HU", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(v); }
};

// ---------- List page ----------
export default function AccidentsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accidents");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8" data-testid="accidents-page">
      <section className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Baleseti jegyzőkönyvek</h1>
          <p className="mt-1 text-sm text-slate-500">
            Munkahelyi balesetek dokumentálása, kivizsgálás és lezárás aláírt jegyzőkönyvvel.
          </p>
        </div>
        <Button data-testid="new-accident-button" onClick={() => setOpenCreate(true)} className="gap-1.5">
          <Plus className="h-4 w-4" /> Új jegyzőkönyv
        </Button>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="border-b border-slate-200/70 bg-slate-50/60 text-slate-500">
            <tr>
              <Th>Leírás</Th>
              <Th>Cég</Th>
              <Th>Telephely</Th>
              <Th>Státusz</Th>
              <Th>Résztvevők</Th>
              <Th>Üzenetek</Th>
              <Th>Rögzítette</Th>
              <Th>Időpont</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400">
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="p-12 text-center text-slate-400">
                <ShieldAlert className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-2 text-sm">Még nincs bejegyzés. Hozd létre az elsőt.</p>
              </td></tr>
            ) : items.map((it) => (
              <tr
                key={it._id}
                data-testid={`accident-row-${it._id}`}
                onClick={() => navigate(`/accidents/${it._id}`)}
                className="cursor-pointer hover:bg-slate-50"
              >
                <Td className="max-w-[28ch] truncate font-medium text-slate-800">
                  {it.description}
                </Td>
                <Td className="text-slate-600">{it.company_name || "—"}</Td>
                <Td className="text-slate-600">{it.site_name || "—"}</Td>
                <Td><StatusBadge status={it.status} /></Td>
                <Td className="text-slate-600">{it.participant_count}</Td>
                <Td className="text-slate-600">{it.comment_count}</Td>
                <Td className="text-slate-600">{it.created_by_name || "—"}</Td>
                <Td className="text-slate-500">{formatDateTimeHu(it.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <CreateAccidentDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        onCreated={() => { setOpenCreate(false); load(); }}
      />
    </div>
  );
}

// ---------- Create dialog ----------
function CreateAccidentDialog({ open, onOpenChange, onCreated }) {
  const [sites, setSites] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [description, setDescription] = useState("");
  const [participants, setParticipants] = useState([]);
  const [incidentAt, setIncidentAt] = useState("");
  const [hasCameraFootage, setHasCameraFootage] = useState(null); // null | true | false
  const [cameraNote, setCameraNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSiteId(""); setDescription(""); setParticipants([]);
    setIncidentAt(""); setHasCameraFootage(null); setCameraNote("");
    (async () => {
      try {
        const [s, c, e] = await Promise.all([
          api.get("/sites"),
          api.get("/companies"),
          api.get("/employees"),
        ]);
        setSites(s.data);
        setCompanies(c.data);
        setEmployees(e.data.filter((x) =>
          ["active", "probation", "notice"].includes(x.status)
        ));
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Betöltés sikertelen.");
      }
    })();
  }, [open]);

  const selectedSite = sites.find((s) => s._id === siteId);
  const selectedCompany = companies.find((c) => c._id === selectedSite?.company_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!siteId) { toast.error("Telephely kiválasztása kötelező."); return; }
    if (!incidentAt) { toast.error("A baleset pontos időpontja kötelező."); return; }
    if (!description.trim()) { toast.error("Leírás megadása kötelező."); return; }
    if (participants.length === 0) { toast.error("Legalább egy résztvevő kötelező."); return; }
    if (hasCameraFootage === null) { toast.error("Add meg, hogy van-e kamerafelvétel."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/accidents", {
        site_id: siteId,
        description: description.trim(),
        participants,
        incident_at: incidentAt,
        has_camera_footage: hasCameraFootage,
        camera_footage_note: hasCameraFootage ? cameraNote.trim() : null,
      });
      toast.success("Jegyzőkönyv létrehozva.");
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="accident-create-dialog">
        <DialogHeader>
          <DialogTitle>Új baleseti jegyzőkönyv</DialogTitle>
          <DialogDescription>
            Telephely kiválasztásával a cég automatikusan hozzárendelődik.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Telephely *</label>
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger data-testid="accident-site-trigger">
                  <SelectValue placeholder="Válassz telephelyet..." />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Cég</label>
              <div
                data-testid="accident-company-readonly"
                className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700"
              >
                <Building2 className="h-4 w-4 text-slate-400" />
                {selectedCompany?.name || <span className="text-slate-400">Telephely választása után</span>}
              </div>
            </div>
          </div>

          <ParticipantsPicker
            employees={employees}
            value={participants}
            onChange={setParticipants}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">
                Baleset pontos időpontja *
              </label>
              <DateTimeInput
                value={incidentAt}
                onChange={setIncidentAt}
                data-testid="accident-incident-at"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">
                Van-e kamerafelvétel? *
              </label>
              <div className="flex gap-2" data-testid="accident-camera-group">
                <button
                  type="button"
                  onClick={() => setHasCameraFootage(true)}
                  data-testid="camera-yes"
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors ${
                    hasCameraFootage === true
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Video className="h-4 w-4" /> Igen
                </button>
                <button
                  type="button"
                  onClick={() => setHasCameraFootage(false)}
                  data-testid="camera-no"
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors ${
                    hasCameraFootage === false
                      ? "border-slate-400 bg-slate-100 text-slate-800 ring-1 ring-slate-300"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <VideoOff className="h-4 w-4" /> Nem
                </button>
              </div>
            </div>
          </div>

          {hasCameraFootage === true && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">
                Kamera megjegyzés (opcionális)
              </label>
              <Input
                value={cameraNote}
                onChange={(ev) => setCameraNote(ev.target.value)}
                placeholder="Pl. Folyosó-3 kamera, 14:20-14:30 közötti felvétel"
                data-testid="accident-camera-note"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Leírás *</label>
            <Textarea
              data-testid="accident-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="Mi történt? Mikor? Milyen körülmények között?"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Mégse
            </Button>
            <Button type="submit" disabled={submitting} data-testid="accident-create-submit">
              {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Létrehozás
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Multi-select participants picker ----------
function ParticipantsPicker({ employees, value, onChange }) {
  const [search, setSearch] = useState("");
  const [extName, setExtName] = useState("");
  const [extRole, setExtRole] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);

  const selectedEmpIds = useMemo(
    () => new Set(value.filter((v) => v.employee_id).map((v) => v.employee_id)),
    [value]
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees
      .filter((e) => !selectedEmpIds.has(e._id))
      .filter((e) => {
        if (!q) return true;
        const full = `${e.last_name || ""} ${e.first_name || ""}`.toLowerCase();
        return full.includes(q);
      })
      .slice(0, 30);
  }, [employees, search, selectedEmpIds]);

  const addEmployee = (emp) => {
    onChange([...value, { employee_id: emp._id, role: "" }]);
    setSearch("");
    setPopoverOpen(false);
  };
  const addExternal = () => {
    if (!extName.trim()) { toast.error("Add meg a külső személy nevét."); return; }
    onChange([...value, { name: extName.trim(), role: extRole.trim() || "" }]);
    setExtName(""); setExtRole("");
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const updateRole = (idx, role) => {
    onChange(value.map((v, i) => (i === idx ? { ...v, role } : v)));
  };

  const displayName = (p) => {
    if (p.employee_id) {
      const emp = employees.find((e) => e._id === p.employee_id);
      return emp ? `${emp.last_name || ""} ${emp.first_name || ""}`.trim() : "—";
    }
    return p.name || "—";
  };

  return (
    <div className="space-y-2" data-testid="accident-participants">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-slate-600">Résztvevők *</label>
        <span className="text-[11px] text-slate-400">{value.length} kiválasztva</span>
      </div>

      {value.length > 0 && (
        <div className="space-y-1.5 rounded-lg border border-slate-200 bg-white p-2">
          {value.map((p, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`participant-row-${idx}`}>
              <Badge variant={p.employee_id ? "default" : "secondary"} className="shrink-0">
                {p.employee_id ? <Users className="mr-1 h-3 w-3" /> : <UserPlus className="mr-1 h-3 w-3" />}
                {displayName(p)}
              </Badge>
              <Select value={p.role || ""} onValueChange={(v) => updateRole(idx, v)}>
                <SelectTrigger
                  className="h-8 flex-1 text-xs"
                  data-testid={`participant-role-trigger-${idx}`}
                >
                  <SelectValue placeholder="Szerep kiválasztása..." />
                </SelectTrigger>
                <SelectContent>
                  {PARTICIPANT_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                data-testid={`remove-participant-${idx}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="justify-start gap-2 text-slate-600"
              data-testid="participants-search-trigger"
            >
              <Search className="h-4 w-4" /> Munkavállaló hozzáadása...
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[360px] p-0" align="start">
            <div className="border-b border-slate-100 p-2">
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Keresés név alapján..."
                className="h-8"
                data-testid="participants-search-input"
              />
            </div>
            <div className="max-h-60 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-slate-400">
                  Nincs találat
                </div>
              ) : filtered.map((emp) => (
                <button
                  type="button"
                  key={emp._id}
                  onClick={() => addEmployee(emp)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                  data-testid={`participant-pick-${emp._id}`}
                >
                  <Users className="h-3.5 w-3.5 text-slate-400" />
                  <span className="flex-1 truncate">
                    {emp.last_name} {emp.first_name}
                  </span>
                  {emp.position && (
                    <span className="text-[11px] text-slate-400">{emp.position}</span>
                  )}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/40 p-3">
        <div className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500">
          <UserPlus className="h-3 w-3" /> Külső résztvevő
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <Input
            value={extName}
            onChange={(e) => setExtName(e.target.value)}
            placeholder="Név"
            className="h-8 text-xs"
            data-testid="external-name"
          />
          <Select value={extRole} onValueChange={setExtRole}>
            <SelectTrigger className="h-8 text-xs" data-testid="external-role-trigger">
              <SelectValue placeholder="Szerep kiválasztása..." />
            </SelectTrigger>
            <SelectContent>
              {PARTICIPANT_ROLES.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button" size="sm" variant="secondary"
            onClick={addExternal}
            data-testid="external-add"
          >
            Hozzáadás
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Detail page ----------
export function AccidentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [sending, setSending] = useState(false);
  const [openClose, setOpenClose] = useState(false);
  const [openFinalize, setOpenFinalize] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [confirmingStatus, setConfirmingStatus] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/accidents/${id}`);
      setItem(data);
    } catch {
      toast.error("A jegyzőkönyv nem található.");
      navigate("/accidents");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const sendComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSending(true);
    try {
      await api.post(`/accidents/${id}/comments`, { message: commentText.trim() });
      setCommentText("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSending(false);
    }
  };

  const moveToInvestigating = async () => {
    setConfirmingStatus(true);
    try {
      await api.post(`/accidents/${id}/status`, { status: "investigating" });
      toast.success("Kivizsgálás alatt státuszra állítva.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Művelet sikertelen.");
    } finally {
      setConfirmingStatus(false);
    }
  };

  const downloadDraft = () => {
    openProtectedFile(
      `/accidents/${id}/draft.pdf`,
      "application/pdf",
      "baleseti-jegyzokonyv-munkapeldany.pdf"
    );
  };
  const downloadSigned = () => {
    openProtectedFile(
      `/accidents/${id}/signed.pdf`,
      item?.signed_pdf?.content_type || "application/pdf",
      item?.signed_pdf?.name || "alairt-jegyzokonyv.pdf"
    );
  };

  if (loading || !item) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8" data-testid="accident-detail-page">
      <button
        onClick={() => navigate("/accidents")}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft className="h-4 w-4" /> Vissza a listához
      </button>

      {item.status === "finalized" && (
        <div
          data-testid="finalized-alert-banner"
          className="group relative overflow-hidden rounded-2xl border border-rose-200/60 bg-gradient-to-br from-rose-50 via-white to-amber-50/30 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(244,63,94,0.25)]"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full bg-rose-200/30 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-16 -left-12 h-40 w-40 rounded-full bg-amber-200/20 blur-3xl"
          />

          <div className="relative flex flex-wrap items-center gap-5 px-5 py-4 sm:px-6">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-rose-200">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-500 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-600" />
              </span>
            </div>

            <div className="min-w-[240px] flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-rose-700/80">
                  Művelet szükséges
                </span>
                <span className="h-px flex-1 bg-rose-200/60" />
              </div>
              <p className="mt-1 text-[15px] font-semibold leading-snug text-slate-900">
                A jegyzőkönyv véglegesítve, de még nincs lezárva.
              </p>
              <p className="mt-0.5 text-[13px] leading-relaxed text-slate-600">
                Töltsd le a munkapéldányt, írasd alá az érintettekkel, majd töltsd fel
                az aláírt PDF-et a végleges lezáráshoz.
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={downloadDraft}
                data-testid="banner-download-draft"
                className="gap-1.5 text-slate-700 hover:bg-white/80 hover:text-slate-900"
              >
                <FileDown className="h-4 w-4" /> Letöltés
              </Button>
              <Button
                size="sm"
                onClick={() => setOpenClose(true)}
                className="gap-1.5 bg-slate-900 text-white shadow-sm hover:bg-slate-800"
                data-testid="banner-open-close-dialog"
              >
                <Upload className="h-4 w-4" /> Aláírt PDF feltöltése
              </Button>
            </div>
          </div>
        </div>
      )}

      <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-500" />
              <h1 className="text-xl font-semibold tracking-tight">
                Baleseti jegyzőkönyv
              </h1>
              <StatusBadge status={item.status} />
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Azonosító: <span className="font-mono">BJK-{item._id?.slice(-6).toUpperCase()}</span>
              {" · "}Rögzítette: <span className="font-medium text-slate-700">{item.created_by_name}</span>
              {" · "}{formatDateTimeHu(item.created_at)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(item.status === "finalized" || item.status === "closed") && (
              <Button variant="outline" size="sm" onClick={downloadDraft} data-testid="download-draft-pdf">
                <FileDown className="mr-1.5 h-4 w-4" /> Munkapéldány PDF
              </Button>
            )}
            {item.status === "closed" && item.signed_pdf && (
              <Button variant="outline" size="sm" onClick={downloadSigned} data-testid="download-signed-pdf">
                <FileText className="mr-1.5 h-4 w-4" /> Aláírt PDF
              </Button>
            )}
            {item.status === "new" && (
              <Button
                size="sm" onClick={moveToInvestigating} disabled={confirmingStatus}
                data-testid="move-investigating"
                className="gap-1.5"
              >
                {confirmingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                Kivizsgálás indítása
              </Button>
            )}
            {item.status === "investigating" && (
              <Button
                size="sm"
                className="bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={() => setOpenFinalize(true)}
                data-testid="open-finalize-dialog"
              >
                <Sparkles className="mr-1.5 h-4 w-4" /> Véglegesítés
              </Button>
            )}
            {item.status === "finalized" && (
              <Button
                size="sm"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={() => setOpenClose(true)}
                data-testid="open-close-dialog"
              >
                <CheckCircle2 className="mr-1.5 h-4 w-4" /> Lezárás (aláírt PDF)
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-3">
          <Field label="Cég" value={item.company_name} />
          <Field label="Telephely" value={item.site_name} />
          <Field label="Státusz" value={STATUS_LABEL[item.status]} />
          <Field label="Baleset időpontja" value={formatDateTimeHu(item.incident_at)} />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Kamerafelvétel</div>
            <div className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-slate-800">
              {item.has_camera_footage === true ? (
                <>
                  <Video className="h-3.5 w-3.5 text-emerald-600" />
                  <span>Igen</span>
                  {item.camera_footage_note && (
                    <span className="text-xs font-normal text-slate-500">· {item.camera_footage_note}</span>
                  )}
                </>
              ) : item.has_camera_footage === false ? (
                <>
                  <VideoOff className="h-3.5 w-3.5 text-slate-400" />
                  <span>Nem</span>
                </>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </div>
          </div>
          {item.closed_at && (
            <>
              <Field label="Lezárás időpontja" value={formatDateTimeHu(item.closed_at)} />
              <Field label="Lezárta" value={item.closed_by_name} />
            </>
          )}
          {item.finalized_at && (
            <>
              <Field label="Véglegesítés időpontja" value={formatDateTimeHu(item.finalized_at)} />
              <Field label="Véglegesítette" value={item.finalized_by_name} />
            </>
          )}
        </div>

        <div className="mt-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Résztvevők ({item.participants?.length || 0})
          </h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {(item.participants || []).map((p, idx) => (
              <Badge
                key={idx}
                variant={p.kind === "employee" ? "default" : "secondary"}
                className="px-2.5 py-1"
                data-testid={`detail-participant-${idx}`}
              >
                {p.kind === "employee" ? <Users className="mr-1 h-3 w-3" /> : <UserPlus className="mr-1 h-3 w-3" />}
                {p.display_name}
                {p.role && <span className="ml-1.5 opacity-70">· {p.role}</span>}
              </Badge>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Baleset leírása
            </h3>
            {(item.status === "new" || item.status === "investigating") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpenEdit(true)}
                data-testid="edit-description"
                className="gap-1.5 text-slate-500 hover:text-slate-900"
              >
                <Pencil className="h-3.5 w-3.5" /> Szerkesztés
              </Button>
            )}
          </div>
          <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
            {item.description}
          </p>
        </div>

        {(item.consequences || item.additional_notes) && (
          <div className="mt-6 grid grid-cols-1 gap-6 rounded-xl bg-emerald-50/40 p-4 ring-1 ring-emerald-100 sm:grid-cols-2">
            {item.consequences && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                  Következmények
                </h3>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{item.consequences}</p>
              </div>
            )}
            {item.additional_notes && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                  Egyéb megjegyzés
                </h3>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{item.additional_notes}</p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Timeline */}
      <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-slate-500" />
          <h2 className="text-sm font-semibold tracking-tight text-slate-700">Idővonal</h2>
        </div>
        <ol className="mt-4 space-y-3" data-testid="accident-timeline">
          {(item.timeline || []).map((t) => (
            <li
              key={t._id}
              className={`relative rounded-lg border p-3 ${
                t.kind === "comment"
                  ? "border-slate-200 bg-white"
                  : "border-slate-100 bg-slate-50/60"
              }`}
              data-testid={`timeline-${t.kind}`}
            >
              <div className="flex items-start gap-2">
                {t.kind === "comment" ? (
                  <MessageSquare className="mt-0.5 h-3.5 w-3.5 text-slate-400" />
                ) : (
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 text-amber-500" />
                )}
                <div className="flex-1">
                  <p className="text-sm text-slate-700">{t.text}</p>
                  <p className="mt-1 text-[11px] text-slate-400">
                    {t.author_name} · {formatDateTimeHu(t.created_at)}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ol>

        {item.status !== "closed" && (
          <form onSubmit={sendComment} className="mt-4 flex items-end gap-2">
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Komment hozzáadása..."
              rows={2}
              data-testid="accident-comment-input"
              className="flex-1"
            />
            <Button type="submit" disabled={sending || !commentText.trim()} data-testid="accident-comment-submit">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        )}
      </section>

      <FinalizeAccidentDialog
        accidentId={id}
        open={openFinalize}
        onOpenChange={setOpenFinalize}
        onFinalized={load}
      />
      <CloseAccidentDialog
        accidentId={id}
        open={openClose}
        onOpenChange={setOpenClose}
        onClosed={load}
      />
      <EditDescriptionDialog
        accidentId={id}
        open={openEdit}
        onOpenChange={setOpenEdit}
        initialDescription={item.description}
        initialIncidentAt={item.incident_at}
        initialHasCamera={item.has_camera_footage}
        initialCameraNote={item.camera_footage_note}
        onSaved={load}
      />
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-0.5 text-sm font-medium text-slate-800">{value || "—"}</div>
  </div>
);

// ---------- Close dialog ----------
// ---------- Edit dialog (description + incident time + camera footage) ----------
function EditDescriptionDialog({ accidentId, open, onOpenChange, initialDescription, initialIncidentAt, initialHasCamera, initialCameraNote, onSaved }) {
  const [description, setDescription] = useState("");
  const [incidentAt, setIncidentAt] = useState("");
  const [hasCamera, setHasCamera] = useState(null);
  const [cameraNote, setCameraNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDescription(initialDescription || "");
      setIncidentAt(initialIncidentAt || "");
      setHasCamera(typeof initialHasCamera === "boolean" ? initialHasCamera : null);
      setCameraNote(initialCameraNote || "");
    }
  }, [open, initialDescription, initialIncidentAt, initialHasCamera, initialCameraNote]);

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error("A leírás nem lehet üres.");
      return;
    }
    if (!incidentAt) {
      toast.error("A baleset időpontja kötelező.");
      return;
    }
    if (hasCamera === null) {
      toast.error("Add meg, hogy van-e kamerafelvétel.");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/accidents/${accidentId}`, {
        description: description.trim(),
        incident_at: incidentAt,
        has_camera_footage: hasCamera,
        camera_footage_note: hasCamera ? cameraNote.trim() : "",
      });
      toast.success("Adatok frissítve.");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="accident-edit-dialog">
        <DialogHeader>
          <DialogTitle>Jegyzőkönyv adatok szerkesztése</DialogTitle>
          <DialogDescription>
            Az adatok véglegesítésig módosíthatók.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Baleset időpontja *</label>
              <DateTimeInput
                value={incidentAt}
                onChange={setIncidentAt}
                data-testid="edit-incident-at"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Kamerafelvétel *</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setHasCamera(true)}
                  data-testid="edit-camera-yes"
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm ${
                    hasCamera === true
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Video className="h-4 w-4" /> Igen
                </button>
                <button
                  type="button"
                  onClick={() => setHasCamera(false)}
                  data-testid="edit-camera-no"
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm ${
                    hasCamera === false
                      ? "border-slate-400 bg-slate-100 text-slate-800 ring-1 ring-slate-300"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <VideoOff className="h-4 w-4" /> Nem
                </button>
              </div>
            </div>
          </div>
          {hasCamera === true && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">
                Kamera megjegyzés (opcionális)
              </label>
              <Input
                value={cameraNote}
                onChange={(ev) => setCameraNote(ev.target.value)}
                placeholder="Pl. Folyosó-3 kamera, 14:20-14:30"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Leírás *</label>
            <Textarea
              data-testid="edit-description-input"
              value={description}
              onChange={(ev) => setDescription(ev.target.value)}
              rows={6}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Mégse
            </Button>
            <Button type="submit" disabled={submitting} data-testid="edit-description-submit">
              {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Mentés
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FinalizeAccidentDialog({ accidentId, open, onOpenChange, onFinalized }) {
  const [consequences, setConsequences] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setConsequences(""); setAdditionalNotes(""); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!consequences.trim() || !additionalNotes.trim()) {
      toast.error("Minden mező kötelező a véglegesítéshez.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/accidents/${accidentId}/finalize`, {
        consequences: consequences.trim(),
        additional_notes: additionalNotes.trim(),
      });
      toast.success("Jegyzőkönyv véglegesítve. Töltsd le, nyomtasd ki, írasd alá!");
      onOpenChange(false);
      onFinalized?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Véglegesítés sikertelen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="accident-finalize-dialog">
        <DialogHeader>
          <DialogTitle>Jegyzőkönyv véglegesítése</DialogTitle>
          <DialogDescription>
            Add meg a következményeket és az egyéb megjegyzéseket. Véglegesítés után a PDF
            letölthető, nyomtatható és aláírható. A lezáráshoz az aláírt PDF feltöltése szükséges.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Következmények leírása *</label>
            <Textarea
              data-testid="finalize-consequences"
              value={consequences}
              onChange={(e) => setConsequences(e.target.value)}
              rows={5}
              placeholder="Pl. munkavédelmi eljárás módosítása, képzés, eszközcsere..."
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Egyéb megjegyzés *</label>
            <Textarea
              data-testid="finalize-notes"
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              rows={4}
              placeholder="További észrevétel, hivatkozás dokumentumokra, iktatószám..."
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Mégse
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-indigo-600 text-white hover:bg-indigo-700"
              data-testid="finalize-submit"
            >
              {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Véglegesítés
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Close dialog (PDF upload only) ----------
function CloseAccidentDialog({ accidentId, open, onOpenChange, onClosed }) {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (!open) setFile(null); }, [open]);

  const downloadDraft = () => {
    openProtectedFile(
      `/accidents/${accidentId}/draft.pdf`,
      "application/pdf",
      "baleseti-jegyzokonyv-munkapeldany.pdf"
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Az aláírt PDF feltöltése kötelező.");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append("signed_pdf", file);
    try {
      await api.post(`/accidents/${accidentId}/close`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Jegyzőkönyv lezárva.");
      onOpenChange(false);
      onClosed?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Lezárás sikertelen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="accident-close-dialog">
        <DialogHeader>
          <DialogTitle>Aláírt jegyzőkönyv feltöltése</DialogTitle>
          <DialogDescription>
            Töltsd le a véglegesített munkapéldányt, nyomtasd ki, írasd alá, szkenneld be
            PDF-be és töltsd fel ide a végleges lezáráshoz.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <button
            type="button"
            onClick={downloadDraft}
            className="flex w-full items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 text-left hover:bg-indigo-50"
            data-testid="close-download-draft"
          >
            <FileDown className="h-5 w-5 text-indigo-600" />
            <div className="flex-1">
              <div className="text-sm font-medium text-indigo-900">Letöltés: munkapéldány PDF</div>
              <div className="text-[11px] text-indigo-700/80">A véglegesített tartalommal — kinyomtatható</div>
            </div>
          </button>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Aláírt jegyzőkönyv (PDF) *</label>
            <div className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50/50 p-3">
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="close-pdf-input"
              />
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => fileRef.current?.click()}
                className="gap-1.5"
                data-testid="close-pdf-pick"
              >
                <Upload className="h-4 w-4" /> Tallózás...
              </Button>
              <span className="flex-1 truncate text-xs text-slate-500">
                {file ? file.name : "Nincs fájl kiválasztva"}
              </span>
              {file && (
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Mégse
            </Button>
            <Button
              type="submit"
              disabled={submitting || !file}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="close-submit"
            >
              {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Lezárás
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
